// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { formatHumanName, getReferenceString } from '@medplum/core';
import type { Appointment, ChargeItem, DocumentReference, Encounter, Patient, Reference } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { getTelehealthInfo } from '@polarissleep/scheduling';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getAppointmentTypeDisplay,
  getPatientFromAppointment,
  getProviderFromAppointment,
  resolveProviderName,
} from '../utils/appointment-formatting';

/** Enriched appointment with related FHIR data */
export interface AppointmentDetails {
  appointment: Appointment;
  encounter: Encounter | undefined;
  patientName: string;
  patientId: string;
  copay: number | undefined;
  paymentStatus: 'paid' | 'pending' | 'n/a';
  providerName: string;
  providerId: string;
  appointmentType: string;
  /** Zoom join URL for virtual appointments (from telehealth extension) */
  joinUrl: string | undefined;
  documents: { title: string; id: string }[];
  /**
   * Required intake forms (e.g. per-appointment-type questionnaires).
   * TODO(appointments-required-forms): back-fill once ActivityDefinition→Questionnaire
   * mapping exists. Until then this is always an empty array and the UI renders a
   * placeholder dash.
   */
  requiredForms: { title: string; url: string }[];
}

/**
 * Enriches a single Appointment with Encounter, ChargeItem, and DocumentReference data.
 * @param medplum - The Medplum client instance
 * @param appointment - The FHIR Appointment resource to enrich
 * @returns Enriched appointment details with copay, payment status, and documents
 */
async function enrichAppointment(
  medplum: ReturnType<typeof useMedplum>,
  appointment: Appointment
): Promise<AppointmentDetails> {
  const provider = getProviderFromAppointment(appointment);
  const patient = getPatientFromAppointment(appointment);
  const providerName = await resolveProviderName(medplum, appointment, provider.name);
  const patientName = await resolvePatientName(medplum, appointment, patient.name);
  const appointmentType = getAppointmentTypeDisplay(appointment);
  const telehealthInfo = getTelehealthInfo(appointment);

  // Base result with no enrichment
  const base: AppointmentDetails = {
    appointment,
    encounter: undefined,
    patientName,
    patientId: patient.id,
    copay: undefined,
    paymentStatus: 'n/a',
    providerName,
    providerId: provider.id,
    appointmentType,
    joinUrl: telehealthInfo?.joinUrl,
    documents: [],
    requiredForms: [],
  };

  if (!appointment.id) {
    return base;
  }

  const encounters = await medplum.searchResources('Encounter', {
    appointment: getReferenceString(appointment),
    _count: '1',
  });

  const encounter = encounters[0];
  if (!encounter?.id) {
    return base;
  }

  base.encounter = encounter;

  // Fetch ChargeItems and DocumentReferences in parallel — use individual
  // .catch so a 403 on one resource doesn't hide the entire appointment.
  const [chargeItems, documentRefs] = await Promise.all([
    medplum
      .searchResources('ChargeItem', {
        context: getReferenceString(encounter),
        _count: '10',
      })
      .catch(() => [] as ChargeItem[]),
    medplum
      .searchResources('DocumentReference', {
        encounter: getReferenceString(encounter),
        _count: '10',
      })
      .catch(() => [] as DocumentReference[]),
  ]);

  if (chargeItems.length > 0) {
    const totalCopay = chargeItems.reduce((sum: number, item: ChargeItem) => sum + (item.priceOverride?.value ?? 0), 0);
    base.copay = totalCopay;
    const allBilled = chargeItems.every((item: ChargeItem) => item.status === 'billed');
    base.paymentStatus = allBilled ? 'paid' : 'pending';
  }

  base.documents = documentRefs
    .filter((doc: DocumentReference) => doc.id)
    .map((doc: DocumentReference) => ({
      title: doc.description ?? doc.type?.text ?? doc.type?.coding?.[0]?.display ?? 'Document',
      id: doc.id as string,
    }));

  return base;
}

async function resolvePatientName(
  medplum: ReturnType<typeof useMedplum>,
  appointment: Appointment,
  fallbackName: string
): Promise<string> {
  if (fallbackName !== 'Patient') {
    return fallbackName;
  }

  const patientReference = appointment.participant?.find((participant) =>
    participant.actor?.reference?.startsWith('Patient/')
  )?.actor;
  if (!patientReference?.reference) {
    return fallbackName;
  }

  try {
    const patient = (await medplum.readReference(patientReference as Reference<Patient>)) as Patient;
    const humanName = patient.name?.find((name) => formatHumanName(name).trim().length > 0);
    return humanName ? formatHumanName(humanName) : fallbackName;
  } catch {
    return fallbackName;
  }
}

/**
 * Hook that enriches an array of Appointment resources with Encounter,
 * ChargeItem (copay/payment), and DocumentReference (linked documents) data.
 *
 * Stable identity is keyed on the concatenated list of appointment IDs so that
 * callers can pass a new array reference each render without re-fetching.
 *
 * @param appointments - Raw FHIR Appointment resources
 * @returns Enriched appointment details, loading state, and error
 */
export function useAppointmentDetails(appointments: Appointment[]): {
  details: AppointmentDetails[];
  loading: boolean;
  error: Error | null;
} {
  const medplum = useMedplum();
  const [details, setDetails] = useState<AppointmentDetails[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Keep the latest `appointments` array accessible via a ref so the callback
  // identity can be keyed on a stable string (the joined id list) — passing the
  // array itself would make the callback re-create on every parent render.
  //
  // Caveat: the re-fetch trigger is the joined id list, so mutating an
  // appointment's content without changing its `id` does NOT re-enrich. That's
  // acceptable for the current read-only enrichment use case — callers that need
  // to reflect mid-flight edits should call `refresh()` explicitly.
  const appointmentsRef = useRef(appointments);
  appointmentsRef.current = appointments;
  const appointmentIds = appointments.map((a) => a.id ?? '').join(',');

  // biome-ignore lint/correctness/useExhaustiveDependencies: appointmentIds is the stable re-fetch trigger; `appointments` is read from a ref to avoid re-running on every parent render.
  const fetchDetails = useCallback(async () => {
    const current = appointmentsRef.current;
    if (current.length === 0) {
      setDetails([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const enriched = await Promise.all(current.map((appt) => enrichAppointment(medplum, appt)));
      setDetails(enriched);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to enrich appointments'));
    } finally {
      setLoading(false);
    }
  }, [medplum, appointmentIds]);

  useEffect(() => {
    fetchDetails().catch(console.error);
  }, [fetchDetails]);

  return { details, loading, error };
}
